import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { XMarkIcon, CloudArrowUpIcon, TrashIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import { useSnackbar } from 'notistack';
import Modal from '../../../../ui/canon/Modal';
import Button from '../../../../ui/canon/Button';
import Input from '../../../../ui/canon/Input';

const UPLOAD_VAULT_DOCUMENT = gql`
  mutation UploadVaultDocument(
    $title: String!
    $description: String
    $category: String!
    $tags: [String!]
    $file: File!
    $shared_with_negotiating: Boolean
    $source_type: String
  ) {
    upload_vault_document(
      title: $title
      description: $description
      category: $category
      tags: $tags
      file: $file
      shared_with_negotiating: $shared_with_negotiating
      source_type: $source_type
    ) {
      id
      title
    }
  }
`;

const DocumentUploadModal = ({
  open,
  onClose,
  onSuccess,
  defaultTitle,
  defaultCategory,
  defaultSourceType,
  templateInfo,
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState(defaultTitle || '');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState(defaultCategory || 'other');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const categories = [
    { value: 'other', label: intl.formatMessage({ id: 'succession.vault.cat_other' }) },
    { value: 'financial', label: intl.formatMessage({ id: 'succession.vault.cat_financial' }) },
    { value: 'legal', label: intl.formatMessage({ id: 'succession.vault.cat_legal' }) },
    { value: 'operational', label: intl.formatMessage({ id: 'succession.vault.cat_operational' }) },
    { value: 'marketing', label: intl.formatMessage({ id: 'succession.vault.cat_marketing' }) },
    { value: 'hr', label: intl.formatMessage({ id: 'succession.vault.cat_hr' }) },
  ];

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB

    const validFiles = selectedFiles.filter(file => {
      if (file.size > maxSize) {
        enqueueSnackbar(intl.formatMessage({ id: 'succession.vault.upload_size_error' }, { name: file.name }), { variant: 'error' });
        return false;
      }
      return true;
    });

    setFiles([...files, ...validFiles]);
    if (validFiles.length > 0 && !title && !defaultTitle) {
      setTitle(validFiles[0].name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleRemoveFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSubmit = (uploadMutation) => {
    if (!title.trim() || files.length === 0) {
      enqueueSnackbar(intl.formatMessage({ id: 'succession.vault.upload_required' }), { variant: 'warning' });
      return;
    }

    setIsUploading(true);

    files.forEach((file, index) => {
      uploadMutation({
        variables: {
          title: index === 0 ? title : `${title} (${index + 1})`,
          description: index === 0 ? description : '',
          category,
          tags: index === 0 ? tags : [],
          file,
          shared_with_negotiating: false,
          source_type: defaultSourceType || null,
        },
        context: { hasUpload: true },
      }).then(() => {
        if (index === files.length - 1) {
          setIsUploading(false);
          enqueueSnackbar(intl.formatMessage({ id: 'succession.vault.upload_success' }), { variant: 'success' });
          handleClose();
          onSuccess();
        }
      }).catch(error => {
        setIsUploading(false);
        enqueueSnackbar(intl.formatMessage({ id: 'succession.vault.upload_error' }, { error: error.message }), { variant: 'error' });
      });
    });
  };

  const handleClose = () => {
    setTitle(defaultTitle || '');
    setDescription('');
    setCategory(defaultCategory || 'other');
    setTags([]);
    setTagInput('');
    setFiles([]);
    setIsUploading(false);
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Mutation mutation={UPLOAD_VAULT_DOCUMENT}>
      {(uploadDocument) => (
        <Modal
          open={open}
          onClose={handleClose}
          className="bg-succession-bg-primary text-succession-text-primary rounded-lg shadow-succession-lg border border-succession-border w-full max-w-2xl animate-modal-scale-in max-h-[90vh] flex flex-col"
          panelClassName="absolute inset-0 flex items-center justify-center p-4 overflow-y-auto"
        >
          <div className="flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center mb-4 flex-shrink-0 px-6 pt-6">
              <h2 className="text-xl font-semibold text-succession-text-primary">
                {intl.formatMessage({ id: 'succession.vault.upload_title' })}
              </h2>
              <button
                onClick={handleClose}
                className="p-1 text-succession-text-secondary hover:text-succession-text-primary hover:bg-succession-hover-bg rounded-md transition-theme duration-theme"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6">
              <div className="space-y-4">
                {templateInfo && (
                  <div className="rounded-lg border border-succession-border bg-succession-bg-secondary p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-succession-text-secondary mb-2">
                      {intl.formatMessage({ id: 'succession.vault.upload_step1' })}
                    </p>
                    <a
                      href={templateInfo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={templateInfo.fileName}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border border-succession-border text-succession-text-primary bg-succession-bg-primary hover:bg-succession-hover-bg transition-theme duration-theme"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4 flex-shrink-0" />
                      {templateInfo.label}
                    </a>
                    <p className="mt-2 text-xs text-succession-text-muted">
                      {intl.formatMessage({ id: 'succession.vault.upload_step1_note' })}
                    </p>
                  </div>
                )}
                {templateInfo && (
                  <p className="text-xs font-semibold uppercase tracking-wide text-succession-text-secondary">
                    {intl.formatMessage({ id: 'succession.vault.upload_step2' })}
                  </p>
                )}
                <div className="border-2 border-dashed border-succession-border rounded-lg p-8 text-center hover:border-succession-primary hover:bg-succession-hover-bg transition-theme duration-theme">
                  <input
                    accept="*/*"
                    style={{ display: 'none' }}
                    id="file-upload"
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                  />
                  <Button
                    variant="outline"
                    size="md"
                    className="mb-2 flex items-center gap-2 mx-auto"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const input = document.getElementById('file-upload');
                      if (input) input.click();
                    }}
                  >
                    <CloudArrowUpIcon className="h-4 w-4" />
                    {intl.formatMessage({ id: 'succession.vault.action_browse' })}
                  </Button>
                  <p className="text-sm text-succession-text-secondary mt-2">
                    {intl.formatMessage({ id: 'succession.vault.upload_drag_drop' })}
                  </p>
                  <p className="text-xs text-succession-text-muted mt-1">
                    {intl.formatMessage({ id: 'succession.vault.upload_formats' })}
                  </p>
                </div>

                {files.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-succession-text-primary">
                      {intl.formatMessage({ id: 'succession.vault.upload_selected' }, { count: files.length })}
                    </h3>
                    {files.map((file, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center p-3 border border-succession-border rounded-md bg-succession-bg-secondary"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-succession-text-primary truncate">{file.name}</p>
                          <p className="text-xs text-succession-text-secondary">{formatFileSize(file.size)}</p>
                        </div>
                        <button
                          onClick={() => handleRemoveFile(index)}
                          className="p-1 text-succession-text-secondary hover:text-red-600 dark:hover:text-red-400 hover:bg-succession-hover-bg rounded-md transition-theme duration-theme ml-2"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-succession-text-primary mb-1">
                    {intl.formatMessage({ id: 'succession.vault.doc_title_label' })} <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={intl.formatMessage({ id: 'succession.vault.doc_title_placeholder' })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-succession-text-primary mb-1">
                    {intl.formatMessage({ id: 'succession.vault.doc_description_label' })}
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted border border-succession-border hover:border-succession-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring rounded-md transition-theme duration-theme text-sm px-3 py-1.5 resize-none"
                    rows={3}
                    placeholder={intl.formatMessage({ id: 'succession.vault.doc_description_placeholder' })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-succession-text-primary mb-1">
                    {intl.formatMessage({ id: 'succession.vault.doc_category_label' })}
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full bg-succession-bg-primary text-succession-text-primary border border-succession-border hover:border-succession-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring rounded-md transition-theme duration-theme text-sm px-3 py-1.5 appearance-none pr-8"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-succession-text-primary mb-1">
                    {intl.formatMessage({ id: 'succession.vault.doc_tags_label' })}
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                      placeholder={intl.formatMessage({ id: 'succession.vault.doc_tags_placeholder' })}
                      className="flex-1"
                    />
                    <Button variant="outline" size="md" onClick={handleAddTag}>
                      {intl.formatMessage({ id: 'succession.vault.action_add' })}
                    </Button>
                  </div>
                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-0.5 text-xs bg-succession-bg-secondary text-succession-text-secondary rounded-md flex items-center gap-1"
                        >
                          {tag}
                          <button
                            onClick={() => handleRemoveTag(tag)}
                            className="hover:text-succession-text-primary"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {isUploading && (
                  <div className="space-y-2">
                    <div className="w-full bg-succession-bg-secondary rounded-full h-2 overflow-hidden">
                      <div className="bg-succession-primary h-full animate-pulse" style={{ width: '50%' }} />
                    </div>
                    <p className="text-sm text-succession-text-secondary">
                      {intl.formatMessage({ id: 'succession.vault.upload_uploading' })}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-succession-border flex-shrink-0 px-6 pb-6">
              <Button variant="outline" onClick={handleClose} disabled={isUploading}>
                {intl.formatMessage({ id: 'succession.vault.action_cancel' })}
              </Button>
              <Button
                variant="primary"
                onClick={() => handleSubmit(uploadDocument)}
                disabled={!title.trim() || files.length === 0 || isUploading}
              >
                {intl.formatMessage({ id: 'succession.vault.action_upload' })}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Mutation>
  );
};

DocumentUploadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default DocumentUploadModal;
